/**
 * DashboardPage — lox247 home.
 *
 * Role-aware:
 *  - Unapproved buyer → redirect to /dashboard/pending-approval
 *  - Approved buyer → "Browse catalogue" CTA + recent orders summary
 *  - Admin (Gordon) → pending approvals + new orders + active products
 */
import { Navigate, Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Spinner } from '@/components/ui/spinner'
import { useUserContext } from '@/client/lib/use-user-context'
import { ShoppingBag, Package, Users, ClipboardList, Receipt } from 'lucide-react'

export function DashboardPage() {
  const { data, isPending } = useUserContext()

  if (isPending) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!data) {
    return (
      <div className="p-6">
        <p className="text-sm text-muted-foreground">
          Could not load your account. Try refreshing.
        </p>
      </div>
    )
  }

  if (!data.isAdmin && !data.isApproved) {
    return <Navigate to="/dashboard/pending-approval" replace />
  }

  if (data.isAdmin) {
    return <AdminDashboard />
  }

  return <BuyerDashboard userName={data.user.name} />
}

function BuyerDashboard({ userName }: { userName: string }) {
  const firstName = userName.split(' ')[0] || 'there'
  return (
    <div className="container mx-auto max-w-5xl space-y-8 p-4 md:p-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">G'day {firstName}.</h1>
        <p className="mt-2 text-muted-foreground">
          Browse the catalogue, build a cart, and submit an order. Gordon will confirm and
          invoice.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ShoppingBag className="h-5 w-5" />
              Catalogue
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Search stock, see what's available, add to cart.
            </p>
            <Button asChild>
              <Link to="/dashboard/catalogue">Open catalogue</Link>
            </Button>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <ClipboardList className="h-5 w-5" />
              My orders
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Order history and current status.
            </p>
            <Button asChild variant="outline">
              <Link to="/dashboard/my-orders">View orders</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function AdminDashboard() {
  return (
    <div className="container mx-auto max-w-5xl space-y-8 p-4 md:p-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold">Admin home</h1>
        <p className="mt-2 text-muted-foreground">
          Manage products, customers, orders, and invoices.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdminTile
          to="/dashboard/admin/products"
          icon={<Package className="h-5 w-5" />}
          title="Products"
          subtitle="Add and manage stock"
        />
        <AdminTile
          to="/dashboard/admin/customers"
          icon={<Users className="h-5 w-5" />}
          title="Customers"
          subtitle="Approve trade buyers"
        />
        <AdminTile
          to="/dashboard/admin/orders"
          icon={<ClipboardList className="h-5 w-5" />}
          title="Orders"
          subtitle="Review and confirm"
        />
        <AdminTile
          to="/dashboard/admin/invoices"
          icon={<Receipt className="h-5 w-5" />}
          title="Invoices"
          subtitle="Generate and send"
        />
      </div>
    </div>
  )
}

function AdminTile({
  to,
  icon,
  title,
  subtitle,
}: {
  to: string
  icon: React.ReactNode
  title: string
  subtitle: string
}) {
  return (
    <Link to={to} className="block group">
      <Card className="transition-colors group-hover:border-primary">
        <CardContent className="pt-6">
          <div className="text-primary">{icon}</div>
          <h2 className="mt-3 font-semibold">{title}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>
        </CardContent>
      </Card>
    </Link>
  )
}
